import { useEffect, useState } from "react";
import { Avatar, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import AutoAwesomeMosaicOutlinedIcon from "@mui/icons-material/AutoAwesomeMosaicOutlined";
import Header from "../../../../components/header/header";
import ExcelFile from "./excel-file/excel-file";
import "./form-builder.css";
import HtmlForm from "./html-form/html-form";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Preview from "./preview/preview";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getClientDetailsRequest, getFormDetailsRequest } from "../../../../redux/clients/slice";
import Loading from "../../../../components/loading/loading";
import { currentUserRequest } from "../../../../redux/users/slice";
import {
  exportClientFormSectionsRequest,
  exportFormTemplateSectionsRequest,
  publishFormToOpenOrdersRequest,
} from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import Notification from "../../../../components/notification/notification";
import ExportTemplateDialog from "./export-dialog";
import ImportTemplateDialog from "./import-dialog";
import PublishDialog from "./publish-dialog";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import {
  getElementUpdated,
  setElementUpdated,
} from "../../../../utils/SingletonStore";
import UnsavedChangesDialog from "./unsaved-changes-dialog";
import eventBus from "../../../../utils/EventBus";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return <>{data?.label}</>;
}

export default function FormBuilder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formId, clientId } = useParams();
  const [value, setValue] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [discardAction, setDiscardAction] = useState("");

  const { loading, clientDetails, formDetails } = useSelector((state: any) => state.clients);
  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    if (!currentUser) {
      dispatch(currentUserRequest());
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (clientId && !clientDetails) {
      dispatch(getClientDetailsRequest({ id: clientId }));
    }
    dispatch(getFormDetailsRequest({ id: formId }));
  }, []); // eslint-disable-line

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (getElementUpdated()) {
      setShowUnsavedChangesDialog(true);
      setDiscardAction(`tabChange-${newValue}`);
    } else {
      setValue(newValue);
    }
  };

  const publishToOpenOrders = async () => {
    setShowLoading(true);
    const response = await publishFormToOpenOrdersRequest(formDetails?.id);
    setShowLoading(false);
    setShowPublishDialog(false);
    if (response && response.status >= 200 && response.status < 300) {
      setShowSuccessNotification(true);
      setSuccessMessage(
        response?.data?.detail ||
        "Updates were published to open orders successfully.",
      );
    } else {
      setShowErrorNotification(true);
      setErrorMessage(
        response?.data?.detail ||
        "Failed to publish updates to open orders. Please try again.",
      );
    }
  };

  const exportFormDownload = async () => {
    try {
      let response;
      setShowLoading(true);
      if (clientId) {
        response = await exportClientFormSectionsRequest(formDetails?.id);
      } else {
        response = await exportFormTemplateSectionsRequest(formDetails?.id);
      }
      setShowLoading(false);
      if (response && response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/json",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${formDetails?.displayName}.json`; // file name
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        setShowExportDialog(false);
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const onDiscardClick = () => {
    setElementUpdated(false);
    setShowUnsavedChangesDialog(false);
    eventBus.emit("hideQuestionSetting");
    if (discardAction === "Export") {
      setShowExportDialog(true);
    }
    if (discardAction === "Import") {
      setShowImportDialog(true);
    }
    if (discardAction === "Publish") {
      setShowPublishDialog(true);
    }
    if (discardAction === "Back") {
      navigate(-1);
    } else if (discardAction.includes("tabChange")) {
      const tabValue = Number(discardAction.slice(-1));
      setValue(tabValue);
    }
  };

  return (
    <>
      <Header />
      <Box className="container">
        <Box sx={{ mx: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{ mt: 2 }}
              variant="text"
              startIcon={<ArrowBackOutlinedIcon color="primary" />}
              onClick={() =>
                getElementUpdated()
                  ? (setShowUnsavedChangesDialog(true),
                    setDiscardAction("Back"))
                  : navigate(-1)
              }
            >
              Back
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {clientId && (
                <Button
                  sx={{ mt: 2, fontWeight: "510" }}
                  variant="outlined"
                  startIcon={<PublishOutlinedIcon />}
                  onClick={() =>
                    getElementUpdated()
                      ? (setShowUnsavedChangesDialog(true),
                        setDiscardAction("Publish"))
                      : setShowPublishDialog(true)
                  }
                >
                  Publish To Open Orders
                </Button>
              )}
              {value === 2 && (
                <Button
                  sx={{ mt: 2, fontWeight: "510" }}
                  variant="contained"
                  startIcon={<DownloadOutlinedIcon />}
                  disabled
                >
                  Test Excel
                </Button>
              )}
            </Box>
          </Box>
          <Typography className="title">{formDetails?.displayName}</Typography>
          {clientId ? (
            <Box display={"flex"}>
              <Avatar
                sx={{ width: 24, height: 24, mr: 1 }}
                alt={clientDetails?.clientName}
                variant="circular"
                src={clientDetails?.logoUrl}
              />
              <Typography className="text-color">
                {clientDetails?.clientName}
              </Typography>
            </Box>
          ) : (
            <Box display={"flex"}>
              <AutoAwesomeMosaicOutlinedIcon className="con-color" />
              <Typography className="text-color">Template</Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{ m: 2 }}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="form builder tabs"
            >
              <Tab
                className="tab-label"
                label={<TabLabel label="Excel File" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Html Form" showWarning={false} />}
              />
              <Tab
                className="tab-label"
                label={<TabLabel label="Preview" showWarning={false} />}
              />
            </Tabs>
          </Box>
          <Box>
            {value === 1 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadOutlinedIcon />}
                  onClick={() =>
                    getElementUpdated()
                      ? (setShowUnsavedChangesDialog(true),
                        setDiscardAction("Export"))
                      : setShowExportDialog(true)
                  }
                >
                  <b>Export form Elements</b>
                </Button>
                <Button
                  sx={{ ml: 2 }}
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() =>
                    getElementUpdated()
                      ? (setShowUnsavedChangesDialog(true),
                        setDiscardAction("Import"))
                      : setShowImportDialog(true)
                  }
                >
                  <b>Import Form Elements</b>
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <CustomTabPanel value={value} index={0}>
        <ExcelFile data={formDetails} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <DndProvider backend={HTML5Backend}>
          <HtmlForm data={formDetails} />
        </DndProvider>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <Preview data={formDetails} />
      </CustomTabPanel>

      {showExportDialog && (
        <ExportTemplateDialog
          show={showExportDialog}
          close={() => setShowExportDialog(false)}
          onExport={exportFormDownload}
        />
      )}
      {showImportDialog && (
        <ImportTemplateDialog
          show={showImportDialog}
          close={() => setShowImportDialog(false)}
          templateId={formDetails?.id}
          isClientForm={formDetails?.clientId ? true : false}
        />
      )}
      {showUnsavedChangesDialog && (
        <UnsavedChangesDialog
          show={showUnsavedChangesDialog}
          close={() => setShowUnsavedChangesDialog(false)}
          onDiscard={onDiscardClick}
        />
      )}

      {showPublishDialog && (
        <PublishDialog
          show={showPublishDialog}
          close={() => setShowPublishDialog(false)}
          onPublish={publishToOpenOrders}
        />
      )}

      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          title={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      )}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
      {(loading || showLoading) && <Loading />}
    </>
  );
}
